import React, { useMemo, useState } from 'react';
import {
  Box,
  Flex,
  SimpleGrid,
  Text,
  Spinner,
  Button,
  Table,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
  TableContainer,
  Collapse,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  PieChart,
  Pie,
  Cell,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import {
  CaretRight,
  FileText,
  CurrencyDollar,
  ArrowSquareIn,
  Receipt,
  Scales,
  Stack,
  ArrowSquareOut,
  Percent,
  ArrowsClockwise,
  Coins,
  Buildings,
  MapPin,
} from 'phosphor-react';
import { useAuditoriaInteligente, IPeriodoParams } from '../../../../hooks/useAuditoriaInteligente';
import { formatCurrency } from '../../../../utils/FormatFunctions';
import { num, qtd } from '../format';
import {
  IAuditoriaEmpresa,
  IFiscalResumoMes,
  IFiscalMesNota,
  IEmpresaDashboard,
  IImpostosApurados,
  IClientesFornecedores,
  IImpostosPorNota,
} from '../types';
import { KpiCard } from '../components/KpiCard';
import { EmptyState } from '../components/EmptyState';
import { InfoBloco, FILTRO } from '../components/InfoPopover';
import { UI } from '../ui';

interface FiscalScreenProps {
  empresa: IAuditoriaEmpresa | null;
  periodo: IPeriodoParams;
  /** Cruzamentos: navegar a outro submódulo (Notas, Impostos, Clientes...). */
  onScreen: (screen: string) => void;
  /** Abre as Notas Fiscais com o período fixado no mês clicado (drill do resumo). */
  onOpenNotasMes: (mes: string) => void;
}

/** Normaliza o `mes` (YYYY-MM ou YYYY-MM-DD) para o formato YYYY-MM-01 da rota de notas. */
const toMesParam = (mes: string): string => (mes.length === 7 ? `${mes}-01` : mes);

/** 'YYYY-MM' → 'MM/AA'. */
const mesLabel = (m: string) => {
  const mm = /^(\d{4})-(\d{2})/.exec(m ?? '');
  return mm ? `${mm[2]}/${mm[1].slice(2)}` : m;
};

const truncar = (s: string, n = 17) => (s && s.length > n ? `${s.slice(0, n)}…` : s);

/** Painel de vidro com título e ação opcional no canto. */
function Panel({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Box
      bg={UI.glass}
      border="1px solid"
      borderColor={UI.glassBorder}
      borderRadius="16px"
      backdropFilter={UI.glassBlur}
      boxShadow={UI.glassShadow}
      p={5}
    >
      <Flex justify="space-between" align="center" mb={4} gap={2}>
        <Text fontSize="13px" fontWeight={800} color={UI.ink}>
          {title}
        </Text>
        {action}
      </Flex>
      {children}
    </Box>
  );
}

/** Bloco de KPIs com rótulo de seção (Receita / Entradas / Tributário). */
function KpiBloco({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box mb={5}>
      <Text
        fontSize="11px"
        fontWeight={700}
        textTransform="uppercase"
        letterSpacing=".7px"
        color={UI.muted}
        mb={2}
      >
        {label}
      </Text>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
        {children}
      </SimpleGrid>
    </Box>
  );
}

/** Barras horizontais (ranking) — top clientes, faturamento por UF. */
function RankingBars({
  data,
  color,
  height = 240,
}: {
  data: { nome: string; valor: number }[];
  color: string;
  height?: number;
}) {
  if (data.length === 0) {
    return (
      <Text fontSize="12px" color={UI.muted} py={10} textAlign="center">
        Sem dados no período.
      </Text>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ left: 6, right: 18, top: 2, bottom: 2 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={UI.border} horizontal={false} />
        <XAxis
          type="number"
          fontSize={10}
          stroke={UI.muted}
          tickFormatter={v => formatCurrency(v)}
        />
        <YAxis
          type="category"
          dataKey="nome"
          width={132}
          fontSize={10.5}
          stroke={UI.muted}
          tickFormatter={(s: string) => truncar(s)}
        />
        <Tooltip formatter={(v: number) => formatCurrency(v)} />
        <Bar dataKey="valor" fill={color} radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Donut de composição (valor por fatia) + legenda com %. */
function Donut({ data }: { data: { name: string; value: number; color: string }[] }) {
  const total = data.reduce((s, x) => s + x.value, 0);
  if (total <= 0) {
    return (
      <Text fontSize="12px" color={UI.muted} py={10} textAlign="center">
        Sem dados no período.
      </Text>
    );
  }
  return (
    <Flex align="center" gap={4} wrap="wrap">
      <ResponsiveContainer width={168} height={168}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            innerRadius={46}
            outerRadius={76}
            paddingAngle={2}
          >
            {data.map(s => (
              <Cell key={s.name} fill={s.color} />
            ))}
          </Pie>
          <Tooltip formatter={(v: number) => formatCurrency(v)} />
        </PieChart>
      </ResponsiveContainer>
      <Box flex={1} minW="130px">
        {data.map(s => (
          <Flex key={s.name} align="center" gap={2} mb={1.5}>
            <Box w="10px" h="10px" borderRadius="full" bg={s.color} flexShrink={0} />
            <Text fontSize="12px" fontWeight={700} color={UI.ink} flex={1}>
              {s.name}
            </Text>
            <Text fontSize="12px" fontWeight={700} color={UI.muted}>
              {((s.value / total) * 100).toFixed(1)}%
            </Text>
          </Flex>
        ))}
      </Box>
    </Flex>
  );
}

function MesNotas({ empresaId, mes }: { empresaId: number; mes: string }) {
  const { getFiscalMesNotas } = useAuditoriaInteligente();
  const { data, isFetching } = useQuery<IFiscalMesNota[]>({
    queryKey: ['ai-fiscal-mes-notas', { id: empresaId, mes: toMesParam(mes) }],
    queryFn: getFiscalMesNotas,
  });

  if (isFetching) {
    return (
      <Box py={4} textAlign="center">
        <Spinner size="sm" />
      </Box>
    );
  }

  const notas = data ?? [];
  if (notas.length === 0) {
    return (
      <Text fontSize="12.5px" color={UI.muted} py={2}>
        Sem notas de saída neste mês.
      </Text>
    );
  }

  return (
    <Table variant="simple" size="sm">
      <Thead>
        <Tr>
          <Th>NF / Série</Th>
          <Th>Data</Th>
          <Th>Parceiro</Th>
          <Th>Situação</Th>
          <Th isNumeric>Valor</Th>
        </Tr>
      </Thead>
      <Tbody>
        {notas.slice(0, 20).map(n => (
          <Tr key={n.codigo}>
            <Td fontWeight={700}>
              {n.numero_nf}
              {n.serie ? ` / ${n.serie}` : ''}
            </Td>
            <Td>{n.data}</Td>
            <Td maxW="240px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              {n.parceiro}
            </Td>
            <Td>{n.situacao}</Td>
            <Td isNumeric>{formatCurrency(n.valor)}</Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}

export function FiscalScreen({ empresa, periodo, onScreen, onOpenNotasMes }: FiscalScreenProps) {
  const {
    getFiscalResumoMensal,
    getEmpresaDashboard,
    getImpostosApurados,
    getClientesFornecedores,
    getImpostosPorNota,
  } = useAuditoriaInteligente();
  const [open, setOpen] = useState<string | null>(null);

  const { data, isFetching } = useQuery<IFiscalResumoMes[]>({
    queryKey: ['ai-fiscal-resumo-mensal', { id: empresa?.id, ...periodo }],
    queryFn: getFiscalResumoMensal,
    enabled: !!empresa,
  });
  const { data: dash } = useQuery<IEmpresaDashboard>({
    queryKey: ['ai-empresa-dashboard', { id: empresa?.id, ...periodo }],
    queryFn: getEmpresaDashboard,
    enabled: !!empresa,
  });
  const { data: imp } = useQuery<IImpostosApurados>({
    queryKey: ['ai-impostos-apurados', { id: empresa?.id, ...periodo }],
    queryFn: getImpostosApurados,
    enabled: !!empresa,
  });
  const { data: clifor } = useQuery<IClientesFornecedores>({
    queryKey: ['ai-clifor-cli', { id: empresa?.id, tipo: 'clientes', ...periodo }],
    queryFn: getClientesFornecedores,
    enabled: !!empresa,
  });
  const { data: impNota } = useQuery<IImpostosPorNota>({
    queryKey: ['ai-imp-nota-saida', { id: empresa?.id, tipo: 'saida', ...periodo }],
    queryFn: getImpostosPorNota,
    enabled: !!empresa,
  });

  const resumo = useMemo(() => data ?? [], [data]);

  const totais = useMemo(
    () =>
      resumo.reduce(
        (acc, m) => ({
          nfs_saida: acc.nfs_saida + num(m.nfs_saida),
          fat_saida: acc.fat_saida + num(m.fat_saida),
          nfs_entrada: acc.nfs_entrada + num(m.nfs_entrada),
          compras: acc.compras + num(m.compras),
          nfs_servico: acc.nfs_servico + num(m.nfs_servico),
          fat_servico: acc.fat_servico + num(m.fat_servico),
        }),
        { nfs_saida: 0, fat_saida: 0, nfs_entrada: 0, compras: 0, nfs_servico: 0, fat_servico: 0 },
      ),
    [resumo],
  );
  // Saldo comercial = receita total (mercadoria + serviço) − compras.
  const saldo = totais.fat_saida + totais.fat_servico - totais.compras;
  const analise = imp?.analise;

  // Só meses com movimento — evita a linha de Saldo despencar a R$ 0 no mês atual/futuro
  // do range que ainda não tem nota lançada.
  const chartData = useMemo(
    () =>
      resumo
        .filter(m => num(m.fat_saida) + num(m.fat_servico) + num(m.compras) > 0)
        .map(m => ({
          mes: mesLabel(m.mes),
          mercadoria: num(m.fat_saida),
          servico: num(m.fat_servico),
          compras: num(m.compras),
          saldo: num(m.fat_saida) + num(m.fat_servico) - num(m.compras),
        })),
    [resumo],
  );

  // Donut: de onde vem (saída + serviço) e quanto sai (compras).
  const composicao = useMemo(
    () =>
      [
        { name: 'Mercadoria', value: totais.fat_saida, color: UI.pos },
        { name: 'Serviço', value: totais.fat_servico, color: '#1AA6B7' },
        { name: 'Compras', value: totais.compras, color: '#8B5CF6' },
      ].filter(x => x.value > 0),
    [totais],
  );

  // Carga tributária por esfera (Federal/Estadual/Municipal/...): agrupa a categoria.
  const cargaEsfera = useMemo(() => {
    const cores: Record<string, string> = {
      Federal: UI.brand,
      Estadual: UI.pos,
      Municipal: UI.warn,
      'Simples Nacional': '#EC4899',
      Retido: '#8B5CF6',
    };
    const extra = ['#1AA6B7', '#F59E0B', '#94A3B8'];
    const by: Record<string, number> = {};
    (dash?.carga_por_categoria ?? []).forEach(c => {
      const esfera = (c.categoria || 'Outros').split(' - ')[0];
      by[esfera] = (by[esfera] ?? 0) + num(c.valor);
    });
    return Object.entries(by)
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([name, value], i) => ({ name, value, color: cores[name] ?? extra[i % extra.length] }));
  }, [dash]);

  const cliBars = useMemo(
    () =>
      (clifor?.ranking ?? [])
        .map(r => ({ nome: r.nome || '—', valor: num(r.total) }))
        .filter(x => x.valor > 0)
        .slice(0, 10),
    [clifor],
  );
  const conc = clifor?.concentracao;

  // Faturamento por UF (destino): prioriza a fonte fiscal (com ICMS); cai p/ clientes.
  const ufBars = useMemo(() => {
    const src =
      impNota?.fonte_disponivel && impNota.por_uf?.length
        ? impNota.por_uf.map(u => ({ nome: u.uf || '—', valor: num(u.faturamento) }))
        : (clifor?.por_uf ?? []).map(u => ({ nome: u.uf || '—', valor: num(u.total) }));
    return src.filter(x => x.valor > 0).slice(0, 10);
  }, [impNota, clifor]);

  if (!empresa) {
    return (
      <EmptyState
        title="Selecione uma empresa"
        message="Use a busca no topo (ID, CNPJ ou Razão Social) para ver a visão geral fiscal."
        icon={FileText}
      />
    );
  }

  return (
    <Box>
      {/* ---------------- KPIs em 3 blocos ---------------- */}
      <KpiBloco label="Receita">
        <KpiCard
          label="Faturamento total"
          value={formatCurrency(dash?.resumo.faturamento ?? totais.fat_saida + totais.fat_servico)}
          sub="mercadoria + serviço"
          icon={CurrencyDollar}
          iconColor={UI.pos}
          iconBg={UI.posSoft}
          isLoading={isFetching}
          onClick={() => onScreen('impostos-por-nota')}
          info={
            <InfoBloco
              oque="Faturamento total na regra do Relatório de Faturamento do Domínio: acumuladores com IFAT = S (mercadoria − devoluções + serviços)."
              dados="extract_acumuladores.saldo (ifat = 'S', mensal) — mês sem extração v2 usa as notas de venda + NFS-e"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Fat. mercadoria"
          value={formatCurrency(totais.fat_saida)}
          sub={`${qtd(totais.nfs_saida)} NFs de venda`}
          icon={FileText}
          iconColor={UI.brand}
          iconBg={UI.infoSoft}
          isLoading={isFetching}
          onClick={() => onScreen('notas')}
          info={
            <InfoBloco
              oque="Faturamento de mercadoria: saídas menos devoluções dos acumuladores com IFAT = S (regra do Domínio)."
              dados="extract_acumuladores (ifat = 'S'): valor_saidas − valor_entradas — mês sem extração v2 usa as notas de venda"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Fat. serviço"
          value={formatCurrency(totais.fat_servico)}
          sub={`${qtd(totais.nfs_servico)} NFS-e`}
          icon={Stack}
          iconColor="#1AA6B7"
          iconBg="#E2F4F6"
          isLoading={isFetching}
          onClick={() => onScreen('notas')}
          info={
            <InfoBloco
              oque="Faturamento de serviço: notas de serviço dos acumuladores com IFAT = S (regra do Domínio)."
              dados="extract_acumuladores.valor_servicos (ifat = 'S') — mês sem extração v2 usa o valor_nota das NFS-e"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Ticket médio"
          value={formatCurrency(dash?.resumo.ticket_medio ?? 0)}
          sub="por nota de saída"
          icon={Receipt}
          iconColor="#F59E0B"
          iconBg="#FDF1DD"
          isLoading={isFetching}
          info={
            <InfoBloco
              oque="Valor médio por nota de venda: valor bruto de venda dividido pela quantidade de notas de venda."
              dados="empresa_resumo.ticket_medio (valor bruto de venda ÷ qtd notas, sem descontar devolução; fallback por notas)"
              filtro={FILTRO.empresa}
            />
          }
        />
      </KpiBloco>

      <KpiBloco label="Entradas & resultado">
        <KpiCard
          label="Compras"
          value={formatCurrency(totais.compras)}
          sub={`${qtd(totais.nfs_entrada)} NFs de entrada`}
          icon={ArrowSquareIn}
          iconColor="#8B5CF6"
          iconBg="#F0EAFB"
          isLoading={isFetching}
          onClick={() => onScreen('clientes-fornecedores')}
          info={
            <InfoBloco
              oque="Soma do valor das notas fiscais de entrada (total de compras/aquisições) no período."
              dados="extract_notas_entrada.valor (SUM por data_entrada)"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Saldo comercial"
          value={formatCurrency(saldo)}
          sub="receita − compras"
          icon={Scales}
          iconColor={saldo >= 0 ? UI.pos : UI.neg}
          iconBg={saldo >= 0 ? UI.posSoft : UI.negSoft}
          isLoading={isFetching}
          info={
            <InfoBloco
              oque="Diferença entre a receita total (mercadoria + serviço) e o total de compras."
              dados="SUM(fat_saida + fat_servico) − SUM(extract_notas_entrada.valor)"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Entradas (cadastro)"
          value={formatCurrency(dash?.resumo.total_entradas ?? totais.compras)}
          sub="total de entradas"
          icon={ArrowSquareIn}
          iconColor={UI.warn}
          iconBg={UI.warnSoft}
          isLoading={isFetching}
          onClick={() => onScreen('notas')}
          info={
            <InfoBloco
              oque="Total de entradas da empresa no período (mesma base de compras, vinda do resumo do dashboard)."
              dados="empresa_resumo.total_entradas (SUM extract_notas_entrada.valor)"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Folha líquida"
          value={formatCurrency(dash?.resumo.folha_liquida ?? 0)}
          sub="no período"
          icon={Coins}
          iconColor="#0EA5E9"
          iconBg="#E0F2FE"
          isLoading={isFetching}
          info={
            <InfoBloco
              oque="Soma do valor líquido da folha de pagamento no período."
              dados="extract_resumo_folha.liquido (SUM por competência)"
              filtro={FILTRO.empresa}
            />
          }
        />
      </KpiBloco>

      <KpiBloco label="Tributário">
        <KpiCard
          label="Carga efetiva"
          value={`${num(analise?.carga_efetiva_pct).toFixed(1)}%`}
          sub="imposto ÷ faturamento"
          icon={Percent}
          iconColor="#8B5CF6"
          iconBg="#F0EAFB"
          isLoading={isFetching}
          onClick={() => onScreen('impostos-apurados')}
          info={
            <InfoBloco
              oque="Carga tributária efetiva: imposto devido sobre o faturamento (mercadoria + serviço)."
              dados="extract_impostos.valor_total ÷ faturamento × 100"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Imposto devido"
          value={formatCurrency(analise?.imposto_devido ?? 0)}
          sub="apurado no período"
          icon={CurrencyDollar}
          iconColor={UI.neg}
          iconBg={UI.negSoft}
          isLoading={isFetching}
          onClick={() => onScreen('impostos-apurados')}
          info={
            <InfoBloco
              oque="Total de imposto devido apurado no período (soma de todos os tributos)."
              dados="extract_impostos.valor_total (SUM no período)"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="A recuperar"
          value={formatCurrency(analise?.a_recuperar ?? 0)}
          sub="crédito potencial"
          icon={ArrowsClockwise}
          iconColor={UI.pos}
          iconBg={UI.posSoft}
          isLoading={isFetching}
          onClick={() => onScreen('cruzamentos')}
          info={
            <InfoBloco
              oque="Crédito potencial a recuperar quando o imposto sobre entradas supera o de saídas."
              dados="extract_impostos: SUM(GREATEST(imposto_entradas − imposto_saidas, 0))"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Total de impostos"
          value={formatCurrency(dash?.resumo.total_impostos ?? 0)}
          sub="soma de tributos"
          icon={Coins}
          iconColor="#EC4899"
          iconBg="#FCE7F1"
          isLoading={isFetching}
          onClick={() => onScreen('impostos-apurados')}
          info={
            <InfoBloco
              oque="Soma de todos os tributos apurados pela empresa no período (DAS, ICMS, PIS, COFINS, ISS, etc.)."
              dados="extract_impostos.valor_total (SUM no período)"
              filtro={FILTRO.empresa}
            />
          }
        />
      </KpiBloco>

      {/* ---------------- Gráfico mensal + composição ---------------- */}
      <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={5} mb={5}>
        {chartData.length > 0 && (
          <Box gridColumn={{ xl: 'span 2' }}>
            <Panel
              title="Faturamento × compras por mês"
              action={
                <Button
                  size="xs"
                  variant="ghost"
                  color={UI.brand}
                  rightIcon={<ArrowSquareOut size={12} weight="bold" />}
                  onClick={() => onScreen('impostos-apurados')}
                >
                  Impostos apurados
                </Button>
              }
            >
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={chartData} maxBarSize={56} barCategoryGap="22%" barGap={6}>
                  <CartesianGrid strokeDasharray="3 3" stroke={UI.border} vertical={false} />
                  <XAxis
                    dataKey="mes"
                    fontSize={11}
                    stroke={UI.muted}
                    interval="preserveStartEnd"
                    padding={{ left: 12, right: 12 }}
                  />
                  <YAxis
                    fontSize={11}
                    stroke={UI.muted}
                    width={70}
                    allowDecimals={false}
                    tickFormatter={v => formatCurrency(v)}
                  />
                  <Tooltip formatter={(v: number) => formatCurrency(v)} />
                  <Legend />
                  <Bar dataKey="mercadoria" name="Mercadoria" stackId="fat" fill={UI.pos} />
                  <Bar
                    dataKey="servico"
                    name="Serviço"
                    stackId="fat"
                    fill="#1AA6B7"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar dataKey="compras" name="Compras" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                  <Line
                    type="monotone"
                    dataKey="saldo"
                    name="Saldo"
                    stroke={UI.brand}
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: UI.brand }}
                    connectNulls={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Panel>
          </Box>
        )}
        <Panel title="Composição da receita">
          <Donut data={composicao} />
        </Panel>
      </SimpleGrid>

      {/* ---------------- Top clientes + Faturamento por UF ---------------- */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} mb={5}>
        <Panel
          title="Top 10 clientes"
          action={
            <Button
              size="xs"
              variant="ghost"
              color={UI.brand}
              leftIcon={<Buildings size={12} weight="bold" />}
              onClick={() => onScreen('clientes-fornecedores')}
            >
              Ver clientes
            </Button>
          }
        >
          <RankingBars data={cliBars} color={UI.brand} />
          {conc && cliBars.length > 0 && (
            <SimpleGrid
              columns={3}
              spacing={2}
              mt={3}
              pt={3}
              borderTop="1px solid"
              borderColor={UI.border}
            >
              {[
                { l: 'Top 1', v: conc.pct_top1 },
                { l: 'Top 5', v: conc.pct_top5 },
                { l: 'Top 10', v: conc.pct_top10 },
              ].map(x => (
                <Box key={x.l} textAlign="center">
                  <Text fontSize="18px" fontWeight={800} color={UI.ink}>
                    {num(x.v).toFixed(1)}%
                  </Text>
                  <Text
                    fontSize="10.5px"
                    fontWeight={700}
                    color={UI.muted}
                    textTransform="uppercase"
                  >
                    {x.l}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          )}
        </Panel>
        <Panel
          title="Faturamento por UF (destino)"
          action={
            <Flex align="center" gap={1} color={UI.muted}>
              <MapPin size={13} weight="bold" />
              <Text fontSize="11px" fontWeight={700}>
                {ufBars.length} UFs
              </Text>
            </Flex>
          }
        >
          <RankingBars data={ufBars} color={UI.pos} />
        </Panel>
      </SimpleGrid>

      {/* ---------------- Carga por esfera ---------------- */}
      {cargaEsfera.length > 0 && (
        <Box mb={5}>
          <Panel
            title="Carga tributária por esfera"
            action={
              <Button
                size="xs"
                variant="ghost"
                color={UI.brand}
                rightIcon={<ArrowSquareOut size={12} weight="bold" />}
                onClick={() => onScreen('impostos-apurados')}
              >
                Impostos apurados
              </Button>
            }
          >
            <Donut data={cargaEsfera} />
          </Panel>
        </Box>
      )}

      {/* ---------------- Resumo mensal ---------------- */}
      <Box
        borderRadius="18px"
        overflow="hidden"
        bg="rgba(255,255,255,0.7)"
        backdropFilter="blur(14px) saturate(160%)"
        border="1px solid rgba(255,255,255,0.85)"
        boxShadow="0 12px 34px -18px rgba(15,23,42,0.35)"
      >
        <Box px={5} pt={4} pb={2}>
          <Text fontSize="13px" fontWeight={700} color={UI.ink}>
            Resumo mensal
          </Text>
          <Text fontSize="12px" color={UI.muted}>
            Clique num mês para ver as notas de saída do período.
          </Text>
        </Box>
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead bg={UI.rowHover}>
              <Tr>
                <Th w="34px" />
                <Th>Mês</Th>
                <Th isNumeric>NFs Saída</Th>
                <Th isNumeric>Fat. Saída</Th>
                <Th isNumeric>NFs Entrada</Th>
                <Th isNumeric>Compras</Th>
                <Th isNumeric>NFs Serviço</Th>
                <Th isNumeric>Fat. Serviço</Th>
                <Th isNumeric>Saldo</Th>
              </Tr>
            </Thead>
            <Tbody>
              {isFetching && (
                <Tr>
                  <Td colSpan={9} textAlign="center" py={8}>
                    <Spinner />
                  </Td>
                </Tr>
              )}
              {!isFetching &&
                resumo.map(m => {
                  const isOpen = open === m.mes;
                  const mSaldo = num(m.fat_saida) + num(m.fat_servico) - num(m.compras);
                  return (
                    <React.Fragment key={m.mes}>
                      <Tr
                        cursor="pointer"
                        _hover={{ bg: UI.rowHover }}
                        bg={isOpen ? '#F0F7FE' : undefined}
                        onClick={() => setOpen(isOpen ? null : m.mes)}
                      >
                        <Td color={UI.muted}>
                          <Box
                            as="span"
                            display="inline-block"
                            transition="transform .15s"
                            transform={isOpen ? 'rotate(90deg)' : undefined}
                          >
                            <CaretRight size={14} />
                          </Box>
                        </Td>
                        <Td fontWeight={700}>{m.mes}</Td>
                        <Td isNumeric>{qtd(m.nfs_saida)}</Td>
                        <Td isNumeric fontWeight={700}>
                          {formatCurrency(m.fat_saida)}
                        </Td>
                        <Td isNumeric>{qtd(m.nfs_entrada)}</Td>
                        <Td isNumeric>{formatCurrency(m.compras)}</Td>
                        <Td isNumeric>{qtd(m.nfs_servico)}</Td>
                        <Td isNumeric>{formatCurrency(m.fat_servico)}</Td>
                        <Td isNumeric fontWeight={700} color={mSaldo >= 0 ? UI.pos : UI.neg}>
                          {formatCurrency(mSaldo)}
                        </Td>
                      </Tr>
                      <Tr>
                        <Td colSpan={9} p={0} border="none">
                          <Collapse in={isOpen} animateOpacity>
                            <Box bg="#F7FAFD" px={6} py={4}>
                              <Flex
                                justify="space-between"
                                align="center"
                                mb={3}
                                wrap="wrap"
                                gap={2}
                              >
                                <Text fontSize="11.5px" color={UI.muted}>
                                  Prévia das notas de saída (top 20). Para ver entradas, serviços e
                                  filtros, abra as Notas Fiscais.
                                </Text>
                                <Button
                                  size="xs"
                                  variant="outline"
                                  color={UI.brand}
                                  borderColor={UI.brand}
                                  rightIcon={<ArrowSquareOut size={12} weight="bold" />}
                                  onClick={() => onOpenNotasMes(m.mes)}
                                >
                                  Abrir nas Notas Fiscais
                                </Button>
                              </Flex>
                              {isOpen && <MesNotas empresaId={empresa.id} mes={m.mes} />}
                            </Box>
                          </Collapse>
                        </Td>
                      </Tr>
                    </React.Fragment>
                  );
                })}
              {!isFetching && resumo.length === 0 && (
                <Tr>
                  <Td colSpan={9} textAlign="center" py={8} color={UI.muted}>
                    Sem movimento fiscal no período.
                  </Td>
                </Tr>
              )}
            </Tbody>
            {!isFetching && resumo.length > 0 && (
              <Tfoot>
                <Tr bg={UI.rowHover} fontWeight={800}>
                  <Td />
                  <Td>Total</Td>
                  <Td isNumeric>{qtd(totais.nfs_saida)}</Td>
                  <Td isNumeric>{formatCurrency(totais.fat_saida)}</Td>
                  <Td isNumeric>{qtd(totais.nfs_entrada)}</Td>
                  <Td isNumeric>{formatCurrency(totais.compras)}</Td>
                  <Td isNumeric>{qtd(totais.nfs_servico)}</Td>
                  <Td isNumeric>{formatCurrency(totais.fat_servico)}</Td>
                  <Td isNumeric color={saldo >= 0 ? UI.pos : UI.neg}>
                    {formatCurrency(saldo)}
                  </Td>
                </Tr>
              </Tfoot>
            )}
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
